import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  DEFAULT_PAGE,
  getActiveNavKey,
  getNavGroupOf,
  getNavItem,
  getVisibleGroups,
} from "./nav-config";
import NavGroups from "./nav-groups";

const MOBILE_QUERY = "(max-width: 767px)";
const CLOSE_ANIMATION_MS = 180;
const SWIPE_CLOSE_DISTANCE = 80;
const FOCUSABLE =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Phone-only Requests navigation: a section bar at the top of the page that
// opens the full nav as a bottom sheet, plus a New request button for the
// My Requests list. Hidden by CSS on wider screens, where the sidebar is used.
function RequestMobileNav({
  pagePermissions,
  permissionsLoaded,
  viewingPage,
  onNewRequest,
}) {
  const [sheetState, setSheetState] = useState("closed"); // closed | open | closing
  const [dragOffset, setDragOffset] = useState(0);

  const triggerRef = useRef(null);
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);
  const dragStartRef = useRef(null);
  const closeTimerRef = useRef(null);

  const activeKey = getActiveNavKey(viewingPage);
  const activeItem = getNavItem(activeKey);
  const activeGroup = getNavGroupOf(activeKey);
  const groups = getVisibleGroups(pagePermissions);
  const isSheetMounted = sheetState !== "closed";

  // Mirrors sheetState so closeSheet can stay stable for effects.
  const sheetStateRef = useRef("closed");
  const updateSheetState = (state) => {
    sheetStateRef.current = state;
    setSheetState(state);
  };

  const openSheet = () => {
    clearTimeout(closeTimerRef.current);
    setDragOffset(0);
    updateSheetState("open");
  };

  const closeSheet = useCallback((returnFocus = true) => {
    if (sheetStateRef.current !== "open") return;
    updateSheetState("closing");
    closeTimerRef.current = setTimeout(() => {
      updateSheetState("closed");
      setDragOffset(0);
      if (returnFocus) triggerRef.current?.focus();
    }, CLOSE_ANIMATION_MS);
  }, []);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  useEffect(() => {
    closeSheet(false);
  }, [viewingPage, closeSheet]);

  // Rotating to landscape or resizing past the phone breakpoint hands
  // navigation back to the sidebar, so the sheet must not linger.
  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const handleChange = (e) => {
      if (!e.matches) closeSheet();
    };
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, [closeSheet]);

  useEffect(() => {
    if (sheetState !== "open") return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const sheet = sheetRef.current;
    const initialFocus =
      sheet?.querySelector('[aria-current="page"]') ||
      sheet?.querySelector(FOCUSABLE);
    initialFocus?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeSheet();
        return;
      }
      if (e.key !== "Tab" || !sheet) return;

      const focusable = [...sheet.querySelectorAll(FOCUSABLE)];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [sheetState, closeSheet]);

  // Swipe down to dismiss. Only starts when the list is scrolled to the top,
  // so scrolling a long nav list doesn't accidentally close the sheet.
  const handleTouchStart = (e) => {
    if (bodyRef.current && bodyRef.current.scrollTop > 0) return;
    dragStartRef.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (dragStartRef.current === null) return;
    const distance = e.touches[0].clientY - dragStartRef.current;
    setDragOffset(Math.max(0, distance));
  };

  const handleTouchEnd = () => {
    if (dragStartRef.current === null) return;
    dragStartRef.current = null;
    if (dragOffset > SWIPE_CLOSE_DISTANCE) {
      closeSheet();
    } else {
      setDragOffset(0);
    }
  };

  const handleNewRequest = () => {
    closeSheet(false);
    onNewRequest();
  };

  return (
    <>
      <div className="rq-mobile-bar">
        <button
          ref={triggerRef}
          type="button"
          className="rq-mobile-bar__trigger"
          onClick={openSheet}
          aria-haspopup="dialog"
          aria-expanded={sheetState === "open"}
        >
          <span className="rq-mobile-bar__labels">
            <span className="rq-mobile-bar__eyebrow">
              {activeGroup?.label || "Requests"}
            </span>
            <span className="rq-mobile-bar__current">
              {activeItem?.label || getNavItem(DEFAULT_PAGE).label}
            </span>
          </span>
          <span className="rq-mobile-bar__hint">
            Switch
            <i className="fa fa-angle-down" aria-hidden="true"></i>
          </span>
        </button>
      </div>

      {viewingPage === "view-requests" && (
        <button
          type="button"
          className="rq-fab"
          onClick={onNewRequest}
          aria-label="New request"
        >
          <i className="fa fa-plus" aria-hidden="true"></i>
        </button>
      )}

      {isSheetMounted &&
        createPortal(
          <div
            className={`rq-sheet ${
              sheetState === "closing" ? "rq-sheet--closing" : ""
            }`}
          >
            <div
              className="rq-sheet__backdrop"
              onClick={() => closeSheet()}
              aria-hidden="true"
            />
            <div
              ref={sheetRef}
              className="rq-sheet__panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="rq-sheet-title"
              style={
                dragOffset && sheetState === "open"
                  ? { transform: `translateY(${dragOffset}px)`, transition: "none" }
                  : undefined
              }
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
            >
              <span className="rq-sheet__handle" aria-hidden="true" />
              <div className="rq-sheet__head">
                <h2 id="rq-sheet-title" className="rq-sheet__title">
                  Requests
                </h2>
                <button
                  type="button"
                  className="rq-sheet__close"
                  onClick={() => closeSheet()}
                  aria-label="Close navigation"
                >
                  <i className="fa fa-times" aria-hidden="true"></i>
                </button>
              </div>

              <nav ref={bodyRef} className="rq-sheet__body" aria-label="Requests">
                <button
                  type="button"
                  className="rq-nav__new"
                  onClick={handleNewRequest}
                >
                  <i className="fa fa-plus" aria-hidden="true"></i>
                  <span className="rq-nav__text">New request</span>
                </button>
                <NavGroups
                  groups={groups}
                  activeKey={activeKey}
                  permissionsLoaded={permissionsLoaded}
                  onNavigate={() => closeSheet(false)}
                />
              </nav>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default RequestMobileNav;
